import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';

import { UserAuthService } from './user-auth.service';

@Injectable({
  providedIn: 'root',
})
export class TokenRegenerationService implements OnDestroy {
  private isRefreshing = false;

  private debounceTimer: any = null;

  private hoverDebounceTimer: any = null;

  private readonly DEBOUNCE_DELAY = 300;

  private readonly HOVER_DEBOUNCE_DELAY = 500;

  private boundHandleUserActivity: () => void;

  private boundHandleHoverActivity: () => void;

  constructor(
    private readonly router: Router,
    private readonly userAuthService: UserAuthService
  ) {
    this.boundHandleUserActivity = this.handleUserActivity.bind(this);
    this.boundHandleHoverActivity = this.handleHoverActivity.bind(this);
    this.initializeActivityDetection();
  }

  private initializeActivityDetection(): void {
    // Use passive listeners for better performance
    const eventOptions = { passive: true, capture: false };

    // Listen for click events
    document.addEventListener(
      'click',
      this.boundHandleUserActivity,
      eventOptions
    );

    // Listen for keydown events (keyboard activity)
    document.addEventListener(
      'keydown',
      this.boundHandleUserActivity,
      eventOptions
    );

    // Listen for touchstart events (mobile touch activity)
    document.addEventListener(
      'touchstart',
      this.boundHandleUserActivity,
      eventOptions
    );

    // Listen for mousemove events (hover activity) with longer debounce
    document.addEventListener(
      'mousemove',
      this.boundHandleHoverActivity,
      eventOptions
    );
  }

  private handleUserActivity(): void {
    // Clear existing timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // Set new timer to debounce the activity check
    this.debounceTimer = setTimeout(() => {
      this.checkAndRefreshToken();
    }, this.DEBOUNCE_DELAY);
  }

  private handleHoverActivity(): void {
    if (this.hoverDebounceTimer) {
      clearTimeout(this.hoverDebounceTimer);
    }

    this.hoverDebounceTimer = setTimeout(() => {
      this.checkAndRefreshToken();
    }, this.HOVER_DEBOUNCE_DELAY);
  }

  private checkAndRefreshToken(): void {
    // Skip if already refreshing or user not logged in
    if (this.isRefreshing || !this.userAuthService.isUserLoggedIn()) {
      return;
    }

    // Check if token expires in 30 minutes or less
    if (this.tokenExpiresSoon()) {
      this.refreshToken();
    }
  }

  public refreshToken(): void {
    if (this.isRefreshing) {
      return;
    }

    this.isRefreshing = true;

    this.userAuthService.refreshToken().subscribe({
      next: (result) => {
        this.isRefreshing = false;
      },
      error: (error) => {
        this.isRefreshing = false;
        // If refresh fails, logout the user and bring them back here once logged in again
        this.userAuthService.logout(true);
      },
    });
  }

  public isTokenRefreshing(): boolean {
    return this.isRefreshing;
  }

  tokenExpiresSoon(): boolean {
    if (!this.userAuthService.isUserLoggedIn()) {
      return false;
    }

    const currentUser = this.userAuthService.getCurrentUser();
    if (!currentUser.expirationTime) {
      return false;
    }

    const expirationTime = Number(currentUser.expirationTime);
    const currentTime = new Date().getTime();
    const thirtyMinutesInMs = 30 * 60 * 1000;

    // Return true if token expires in 30 minutes or less
    return expirationTime - currentTime <= thirtyMinutesInMs;
  }

  ngOnDestroy(): void {
    // Clean up event listeners
    document.removeEventListener('click', this.boundHandleUserActivity);
    document.removeEventListener('keydown', this.boundHandleUserActivity);
    document.removeEventListener('touchstart', this.boundHandleUserActivity);
    document.removeEventListener('mousemove', this.boundHandleHoverActivity);

    // Clear debounce timers
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    if (this.hoverDebounceTimer) {
      clearTimeout(this.hoverDebounceTimer);
      this.hoverDebounceTimer = null;
    }
  }
}
